import { COMPONENT_TYPES } from "helpers/utils/constants";
import { schema } from "./data";
import { ENDPOINTS } from "store/api/endpoints";

// REMOVE VALIDATION FOR LIVE EVENT URL IF LIVE EVENT NOT ENABLED
export const removeLiveEventValidation = (errors, data) => {
  if (!data?.is_live_event_enabled) {
    errors.delete("live_event_url");
  }
  return errors;
};

// REMOVE VALIDATION ERRORS FOR HIDDEN CAMPUS CONNECT FIELDS BASED ON CONDITIONS
export const removeHiddenFieldValidationError = (errors, data) => {
  const campusConnectStoryFieldName = schema
    .filter((item) => item.name.includes("_story"))
    .map((item) => item.name);

  const campusConnectJobFieldName = schema
    .filter((item) => {
      return (
        item.name.includes("campus_connect") &&
        !campusConnectStoryFieldName.includes(item.name)
      );
    })
    .map((item) => item.name);

  if (!data?.campus_connect) {
    const campusConnect = schema
      .filter((item) => item.name.includes("campus_connect"))
      .map((item) => item.name);
    campusConnect.forEach((item) => {
      errors.delete(item);
    });
  }

  if (data?.integrate_campus_connect === "job") {
    campusConnectStoryFieldName.forEach((item) => {
      if (item !== "campus_connect_schools") {
        errors.delete(item);
      }
    });
  }

  if (data?.integrate_campus_connect === "story") {
    campusConnectJobFieldName.forEach((item) => {
      if (item !== "campus_connect_schools") {
        errors.delete(item);
      }
    });
  }

  if (
    ["both", "job"].includes(data?.integrate_campus_connect) &&
    [undefined, "Via Employer Website"].includes(
      data?.campus_connect_preferred_response
    )
  ) {
    errors.delete("campus_connect_application_email");
  }

  if (
    ["both", "story"].includes(data?.integrate_campus_connect) &&
    data?.campus_connect_story_media_type === "VIDEO"
  ) {
    errors.delete("campus_connect_story_image");
    errors.delete("campus_connect_story_image_title");
    errors.delete("campus_connect_story_image_description");
  }

  if (
    ["both", "story"].includes(data?.integrate_campus_connect) &&
    data?.campus_connect_story_media_type === "IMAGE"
  ) {
    errors.delete("campus_connect_story_video_youtube_url");
  }

  return errors;
};

// POPULATE INITIAL VALUES FOR CAMPUS CONNECT FORM BASED ON JOBS, STORY, AND STATUS
export const populateCampusConnectInitialValue = (jobs, story, status) => {
  const campusConnectStatus = status !== "no" ? true : false;
  const salaryPaymentFrequencyMapping = ["zero", "one", "two", "three"];
  const school = jobs?.schools ?? story?.requirements?.schools;
  return {
    integrate_campus_connect: status === "no" ? "job" : status,
    campus_connect: campusConnectStatus,
    campus_connect_job_title: jobs?.position,
    campus_connect_job_type: jobs?.type,
    campus_connect_no_of_position: jobs?.no_of_position,
    campus_connect_hours_per_week: jobs?.hours_per_week,
    campus_connect_salary_amount: jobs?.salary_amount,
    campus_connect_salary_payment_frequency:
      salaryPaymentFrequencyMapping[jobs?.salary_payment_frequency],
    campus_connect_province: jobs?.province,
    campus_connect_city: jobs?.city,
    campus_connect_description: jobs?.description,
    campus_connect_education_level: jobs?.education?.level,
    campus_connect_education_study_years: jobs?.education?.study_years,
    campus_connect_education_subject_areas: jobs?.education?.subject_areas,
    campus_connect_qualification: jobs?.qualifications,
    campus_connect_deadline: jobs?.deadline,
    campus_connect_preferred_response: jobs?.preferred_response,
    campus_connect_schools: school?.map((item) => ({
      key: item,
      value: item,
    })),
    campus_connect_application_email:
      jobs?.application_email !== "null" ? jobs?.application_email : "",
    campus_connect_application_instructions: jobs?.application_instructions,
    campus_connect_story_title: story?.title,
    campus_connect_story_body: story?.body,
    campus_connect_story_media_type: story?.media_type ?? "IMAGE",
    campus_connect_story_image_title: story?.image?.title ?? "",
    campus_connect_story_image_description: story?.image?.description ?? "",
    campus_connect_story_image: story?.image?.file_url,
    campus_connect_story_video_youtube_url:
      story?.video?.youtube_url !== "null" ? story?.video?.youtube_url : "",
  };
};

// FETCH CHALLENGE AND CHALLENGE PATH LISTS BASED ON SEARCH AND TABS
export const handleFetchChallengeAssociateList = async (
  setChallenges,
  challengeFetcher,
  search = "",
  tabs
) => {
  if (setChallenges) setChallenges((prev) => ({ ...prev, loading: true }));
  let challenge = [];
  let path = [];

  if (!tabs || tabs === 0 || tabs === 1) {
    challenge = await challengeFetcher({ search });
  }
  if (!tabs || tabs === 0 || tabs === 2) {
    path = await challengeFetcher({ search }, true);
  }

  const all = [
    ...challenge?.map((item) => ({
      ...item,
      type: COMPONENT_TYPES.CHALLENGE,
    })),
    ...path?.map((item) => ({
      ...item,
      uuid: item?.id,
      type: COMPONENT_TYPES.CHALLENGE_PATH,
    })),
  ];
  if (setChallenges)
    setChallenges((prev) => ({
      all,
      selected: prev?.selected,
      loading: false,
    }));
  return all;
};

// FETCH AND RETURN ASSOCIATED ITEMS BASED ON TYPE AND PARENT ID
export const handleSetSelectedAssociations = async (
  fetcher,
  type,
  parent,
  id
) => {
  const associationFilters = { send_all: "yes" };

  switch (type) {
    case COMPONENT_TYPES.LAB: {
      const [labs] = await fetcher(
        parent,
        id,
        COMPONENT_TYPES.LAB,
        associationFilters
      );
      return labs;
    }
    case COMPONENT_TYPES.CHALLENGE: {
      const [challenges] = await fetcher(
        parent,
        id,
        COMPONENT_TYPES.CHALLENGE,
        associationFilters
      );
      const [challengePaths] = await fetcher(
        parent,
        id,
        COMPONENT_TYPES.CHALLENGE_PATH,
        associationFilters
      );

      return [
        ...(challenges || [])?.map((item) => ({
          uuid: item.uuid,
          title: item.title,
          media: item.media,
          type: COMPONENT_TYPES.CHALLENGE,
        })),
        ...(challengePaths || [])?.map((item) => ({
          uuid: item.uuid,
          title: item.title,
          media: item.media,
          type: COMPONENT_TYPES.CHALLENGE_PATH,
        })),
      ];
    }
    case COMPONENT_TYPES.RESOURCE: {
      const [resourceModules] = await fetcher(
        COMPONENT_TYPES.LAB,
        id,
        ENDPOINTS.RESOURCE_MODULE,
        associationFilters
      );
      const [resourceCollections] = await fetcher(
        COMPONENT_TYPES.LAB,
        id,
        COMPONENT_TYPES.RESOURCE_COLLECTION,
        associationFilters
      );
      const [resourcegroups] = await fetcher(
        COMPONENT_TYPES.LAB,
        id,
        COMPONENT_TYPES.RESOURCE_GROUP,
        associationFilters
      );

      return [
        ...(resourceModules || []).map((item) => ({
          uuid: item.uuid,
          title: item.title,
          media: item.cover_image,
          type: "module",
        })),
        ...(resourceCollections || []).map((item) => ({
          uuid: item.uuid,
          title: item.title,
          media: item.cover_image,
          type: "collection",
        })),
        ...(resourcegroups || []).map((item) => ({
          uuid: item.uuid,
          title: item.title,
          media: item.cover_image,
          type: "group",
        })),
      ];
    }
    default:
      return [];
  }
};

// FETCH RESOURCE MODULES, COLLECTIONS AND GROUPS BASED ON SEARCH AND TAB
export const handleFetchResourceAssociateList = async (
  setResources,
  resourceFetcher,
  resourceGroupFetcher,
  search = "",
  tab
) => {
  if (setResources) setResources((prev) => ({ ...prev, loading: true }));
  let res = [];
  let res2 = [];
  let res3 = [];

  if (!tab || tab === 0 || tab === 1) {
    res = await resourceFetcher(false, { search });
  }
  if (!tab || tab === 0 || tab === 2) {
    res2 = await resourceFetcher(true, { search });
  }
  if (!tab || tab === 0 || tab === 3) {
    res3 = await resourceGroupFetcher({ search });
  }

  const all = [
    ...res?.map((item) => ({ ...item, type: "module" })),
    ...res2?.map((item) => ({ ...item, type: "collection" })),
    ...res3?.map((item) => ({ ...item, type: "group" })),
  ];

  if (setResources)
    setResources((prev) => ({
      all,
      selected: prev.selected,
      loading: false,
    }));

  return all;
};
